<template>
  <section
    class="boryx-hero"
    :class="isPc ? 'boryx-hero--pc' : 'boryx-hero--mobile'"
    @mouseenter="pauseAutoplay"
    @mouseleave="startAutoplay"
  >
    <div class="boryx-hero__shell">
      <div
        v-if="isPc"
        class="boryx-hero__ambient"
        :style="{ backgroundImage: 'url(' + activeSlide.image + ')' }"
      ></div>
      <div class="boryx-hero__grid"></div>

      <div v-if="isPc" class="boryx-hero__copy">
        <div class="boryx-hero__brand">
          <img src="@/assets/img/boryx_logo.png" alt="Boryx" />
          <div>
            <strong>铂域交易所</strong>
            <span>BORYX EXCHANGE</span>
          </div>
        </div>

        <transition name="boryx-copy" mode="out-in">
          <div :key="activeSlide.key" class="boryx-hero__message">
            <div class="boryx-hero__eyebrow">
              <span></span>{{ localized(activeSlide, 'eyebrow') }}
            </div>
            <h1>
              <span v-for="line in localized(activeSlide, 'title')" :key="line">{{ line }}</span>
            </h1>
            <p>{{ localized(activeSlide, 'description') }}</p>
          </div>
        </transition>

        <div class="boryx-hero__actions">
          <button class="boryx-button boryx-button--gold" type="button" @click="go('/market')">
            {{ isChinese ? '探索市场' : 'Explore Markets' }}
          </button>
          <button class="boryx-button boryx-button--ghost" type="button" @click="go('/pass/register')">
            {{ isChinese ? '立即注册' : 'Create Account' }}
          </button>
        </div>

        <div class="boryx-hero__stats">
          <div v-for="stat in stats" :key="stat.value">
            <strong>{{ stat.value }}</strong>
            <span>{{ isChinese ? stat.zh : stat.en }}</span>
          </div>
        </div>
      </div>

      <div
        class="boryx-hero__visual"
        @touchstart="onTouchStart"
        @touchend="onTouchEnd"
      >
        <button
          v-if="isPc"
          class="boryx-hero__arrow boryx-hero__arrow--prev"
          type="button"
          aria-label="Previous slide"
          @click="previousSlide"
        >
          ‹
        </button>

        <div class="boryx-hero__poster-frame">
          <transition name="boryx-poster" mode="out-in">
            <img
              :key="activeSlide.key"
              class="boryx-hero__poster"
              :src="activeSlide.image"
              :alt="localized(activeSlide, 'alt')"
            />
          </transition>
          <span class="boryx-hero__frame-corner boryx-hero__frame-corner--tl"></span>
          <span class="boryx-hero__frame-corner boryx-hero__frame-corner--br"></span>
        </div>

        <button
          v-if="isPc"
          class="boryx-hero__arrow boryx-hero__arrow--next"
          type="button"
          aria-label="Next slide"
          @click="nextSlide"
        >
          ›
        </button>

        <div class="boryx-hero__dots" aria-label="Carousel navigation">
          <button
            v-for="(slide, index) in slides"
            :key="slide.key"
            type="button"
            :class="{ active: index === currentSlide }"
            :aria-label="'Slide ' + (index + 1)"
            @click="setSlide(index)"
          ></button>
        </div>
      </div>

      <div v-if="!isPc" class="boryx-hero__mobile-meta">
        <div>
          <span>{{ localized(activeSlide, 'eyebrow') }}</span>
          <strong>{{ localized(activeSlide, 'mobileTitle') }}</strong>
        </div>
        <button type="button" @click="go('/market')">{{ isChinese ? '进入市场' : 'Markets' }} →</button>
      </div>
    </div>
  </section>
</template>

<script>
import bannerGlobal from "@/assets/img/boryx_home/banner-global.png";
import bannerFuture from "@/assets/img/boryx_home/banner-future.png";
import bannerTrading from "@/assets/img/boryx_home/banner-trading.png";
import bannerSecurity from "@/assets/img/boryx_home/banner-security.png";

export default {
  name: "BoryxHomeHero",
  props: {
    isPc: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentSlide: 0,
      autoplayTimer: null,
      touchStartX: 0,
      slides: [
        {
          key: "global",
          image: bannerGlobal,
          eyebrowZh: "全球数字资产服务平台",
          eyebrowEn: "GLOBAL DIGITAL ASSET PLATFORM",
          titleZh: ["连接全球", "交易无界"],
          titleEn: ["Connecting the World", "Trading Without Limits"],
          mobileTitleZh: "连接全球 · 交易无界",
          mobileTitleEn: "Global Access · Limitless Trading",
          descriptionZh: "面向全球用户，提供安全、稳定、专业的一站式数字资产服务。",
          descriptionEn: "Secure, stable and professional digital asset services for users worldwide.",
          altZh: "铂域交易所连接全球宣传海报",
          altEn: "Boryx global trading campaign",
        },
        {
          key: "future",
          image: bannerFuture,
          eyebrowZh: "安全 · 高效 · 透明 · 创新",
          eyebrowEn: "SECURE · EFFICIENT · TRANSPARENT · INNOVATIVE",
          titleZh: ["开启数字资产", "新未来"],
          titleEn: ["Open a New Future", "of Digital Assets"],
          mobileTitleZh: "开启数字资产新未来",
          mobileTitleEn: "A New Future for Digital Assets",
          descriptionZh: "以高效基础设施与全天候支持，连接更广阔的数字金融未来。",
          descriptionEn: "High-performance infrastructure and always-on support for the future of digital finance.",
          altZh: "铂域交易所数字资产未来宣传海报",
          altEn: "Boryx digital asset future campaign",
        },
        {
          key: "trading",
          image: bannerTrading,
          eyebrowZh: "多元产品 · 专业交易",
          eyebrowEn: "MULTI-ASSET · PROFESSIONAL TRADING",
          titleZh: ["轻松交易", "把握每一个机会"],
          titleEn: ["Trade with Ease", "Capture Every Opportunity"],
          mobileTitleZh: "轻松交易 · 把握机会",
          mobileTitleEn: "Trade Easily · Seize Opportunity",
          descriptionZh: "覆盖多种交易产品，以清晰行情与快捷操作满足不同投资需求。",
          descriptionEn: "Multiple products, clear market data and fast execution for every trading strategy.",
          altZh: "铂域交易所多产品交易宣传海报",
          altEn: "Boryx multi-product trading campaign",
        },
        {
          key: "security",
          image: bannerSecurity,
          eyebrowZh: "多层防护 · 实时风控",
          eyebrowEn: "MULTI-LAYER PROTECTION · REAL-TIME RISK CONTROL",
          titleZh: ["多重安全保障", "资产安心无忧"],
          titleEn: ["Layered Security", "Confidence for Every Asset"],
          mobileTitleZh: "多重安全保障 · 资产安心无忧",
          mobileTitleEn: "Layered Security · Asset Confidence",
          descriptionZh: "冷钱包、多重签名、风险控制与实时监测，共同守护账户与资产安全。",
          descriptionEn: "Cold storage, multi-signature controls and real-time monitoring protect every account.",
          altZh: "铂域交易所多重安全保障宣传海报",
          altEn: "Boryx layered security campaign",
        },
      ],
      stats: [
        { value: "100+", zh: "数字资产", en: "Digital Assets" },
        { value: "24/7", zh: "专业支持", en: "Professional Support" },
        { value: "99.9%", zh: "系统可用性", en: "System Uptime" },
      ],
    };
  },
  computed: {
    activeSlide() {
      return this.slides[this.currentSlide];
    },
    isChinese() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "zh";
      return String(locale).toLowerCase().indexOf("zh") === 0;
    },
  },
  mounted() {
    this.startAutoplay();
  },
  beforeDestroy() {
    this.pauseAutoplay();
  },
  methods: {
    localized(slide, field) {
      return slide[field + (this.isChinese ? "Zh" : "En")];
    },
    go(path) {
      if (this.$route && this.$route.path === path) return;
      this.$router.push(path);
    },
    setSlide(index) {
      this.currentSlide = index;
      this.restartAutoplay();
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      this.restartAutoplay();
    },
    previousSlide() {
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.restartAutoplay();
    },
    startAutoplay() {
      if (this.autoplayTimer) return;
      this.autoplayTimer = window.setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      }, 5200);
    },
    pauseAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    restartAutoplay() {
      this.pauseAutoplay();
      this.startAutoplay();
    },
    onTouchStart(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      this.touchStartX = touch ? touch.clientX : 0;
      this.pauseAutoplay();
    },
    onTouchEnd(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      const endX = touch ? touch.clientX : this.touchStartX;
      const distance = endX - this.touchStartX;
      if (Math.abs(distance) > 45) {
        if (distance < 0) this.nextSlide();
        else this.previousSlide();
      } else {
        this.startAutoplay();
      }
    },
  },
};
</script>

<style scoped>
.boryx-hero {
  position: relative;
  color: #f5f6f8;
  font-family: "Mabry Pro", "Microsoft YaHei", Arial, sans-serif;
}

.boryx-hero__shell {
  position: relative;
  width: 12rem;
  min-height: 5.75rem;
  margin: 0 auto;
  padding: 0.48rem 0.55rem 0.44rem;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 4rem;
  align-items: center;
  gap: 0.42rem;
  overflow: hidden;
  border: 1px solid rgba(232, 201, 138, 0.32);
  border-radius: 0.28rem;
  background:
    radial-gradient(circle at 78% 58%, rgba(201, 154, 77, 0.2), transparent 27%),
    radial-gradient(circle at 8% 0%, rgba(217, 221, 227, 0.08), transparent 25%),
    linear-gradient(135deg, #030507 0%, #080d14 54%, #05070b 100%);
  box-shadow: 0 0.38rem 0.9rem rgba(0, 0, 0, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.boryx-hero__ambient {
  position: absolute;
  inset: -0.5rem;
  background-position: 82% 50%;
  background-repeat: no-repeat;
  background-size: 4.8rem auto;
  opacity: 0.13;
  filter: blur(0.3rem) saturate(0.7);
  transform: scale(1.08);
  transition: background-image 0.45s ease;
}

.boryx-hero__ambient::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #030507 13%, rgba(3, 5, 7, 0.92) 46%, rgba(3, 5, 7, 0.35) 100%);
}

.boryx-hero__grid {
  position: absolute;
  inset: 0;
  opacity: 0.16;
  background-image:
    linear-gradient(rgba(232, 201, 138, 0.09) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 201, 138, 0.09) 1px, transparent 1px);
  background-size: 0.48rem 0.48rem;
  mask-image: linear-gradient(90deg, rgba(0, 0, 0, 0.62), transparent 66%);
}

.boryx-hero__copy,
.boryx-hero__visual,
.boryx-hero__mobile-meta {
  position: relative;
  z-index: 2;
}

.boryx-hero__copy {
  min-width: 0;
  padding: 0.08rem 0 0.02rem 0.12rem;
}

.boryx-hero__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.12rem;
  margin-bottom: 0.38rem;
}

.boryx-hero__brand img {
  width: 0.55rem;
  height: 0.55rem;
  object-fit: contain;
}

.boryx-hero__brand div {
  display: flex;
  flex-direction: column;
  gap: 0.03rem;
}

.boryx-hero__brand strong {
  color: #f5f6f8;
  font-size: 0.18rem;
  line-height: 1;
  letter-spacing: 0.03em;
}

.boryx-hero__brand span {
  color: #c99a4d;
  font-size: 0.1rem;
  line-height: 1;
  letter-spacing: 0.22em;
}

.boryx-hero__eyebrow {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  margin-bottom: 0.16rem;
  color: #e8c98a;
  font-size: 0.13rem;
  font-weight: 600;
  letter-spacing: 0.12em;
}

.boryx-hero__eyebrow span {
  width: 0.34rem;
  height: 1px;
  background: linear-gradient(90deg, #c99a4d, #f2d79a);
  box-shadow: 0 0 0.12rem rgba(242, 184, 91, 0.65);
}

.boryx-hero__message {
  min-height: 2.22rem;
}

.boryx-hero__message h1 {
  margin: 0;
  color: #f5f6f8;
  font-size: 0.57rem;
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.025em;
  text-shadow: 0 0.1rem 0.32rem rgba(0, 0, 0, 0.45);
}

.boryx-hero__message h1 span {
  display: block;
}

.boryx-hero__message h1 span:first-child {
  background: linear-gradient(100deg, #f6f7f9 6%, #d9dde3 48%, #e8c98a 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.boryx-hero__message p {
  width: 5.45rem;
  max-width: 100%;
  margin: 0.2rem 0 0;
  color: #9ca4af;
  font-size: 0.16rem;
  line-height: 1.75;
}

.boryx-hero__actions {
  display: flex;
  align-items: center;
  gap: 0.14rem;
  margin-top: 0.24rem;
}

.boryx-button {
  min-width: 1.48rem;
  height: 0.46rem;
  padding: 0 0.24rem;
  border-radius: 0.12rem;
  font-size: 0.15rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.boryx-button:hover {
  transform: translateY(-2px);
}

.boryx-button--gold {
  border: 1px solid #e8c98a;
  color: #090b0e;
  background: linear-gradient(135deg, #f0d59d 0%, #c89542 55%, #f1cb82 100%);
  box-shadow: 0 0.12rem 0.34rem rgba(201, 154, 77, 0.22);
}

.boryx-button--ghost {
  border: 1px solid rgba(217, 221, 227, 0.3);
  color: #e9ebee;
  background: rgba(255, 255, 255, 0.035);
}

.boryx-button--ghost:hover {
  border-color: rgba(232, 201, 138, 0.65);
}

.boryx-hero__stats {
  display: flex;
  align-items: center;
  gap: 0.34rem;
  margin-top: 0.32rem;
}

.boryx-hero__stats div {
  min-width: 1rem;
  padding-right: 0.3rem;
  border-right: 1px solid rgba(232, 201, 138, 0.18);
}

.boryx-hero__stats div:last-child {
  border-right: 0;
}

.boryx-hero__stats strong,
.boryx-hero__stats span {
  display: block;
}

.boryx-hero__stats strong {
  color: #f0d59d;
  font-size: 0.23rem;
  line-height: 1.15;
}

.boryx-hero__stats span {
  margin-top: 0.05rem;
  color: #818a96;
  font-size: 0.11rem;
}

.boryx-hero__visual {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.boryx-hero__poster-frame {
  position: relative;
  width: 3rem;
  aspect-ratio: 9 / 16;
  padding: 0.08rem;
  overflow: hidden;
  border: 1px solid rgba(232, 201, 138, 0.5);
  border-radius: 0.2rem;
  background: linear-gradient(145deg, rgba(236, 211, 161, 0.12), rgba(255, 255, 255, 0.015));
  box-shadow: 0 0.2rem 0.7rem rgba(0, 0, 0, 0.6), 0 0 0.42rem rgba(201, 154, 77, 0.12);
}

.boryx-hero__poster {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.14rem;
}

.boryx-hero__frame-corner {
  position: absolute;
  width: 0.34rem;
  height: 0.34rem;
  pointer-events: none;
}

.boryx-hero__frame-corner--tl {
  top: 0.04rem;
  left: 0.04rem;
  border-top: 2px solid #f0d59d;
  border-left: 2px solid #f0d59d;
  border-radius: 0.08rem 0 0;
}

.boryx-hero__frame-corner--br {
  right: 0.04rem;
  bottom: 0.04rem;
  border-right: 2px solid #c89542;
  border-bottom: 2px solid #c89542;
  border-radius: 0 0 0.08rem;
}

.boryx-hero__arrow {
  position: absolute;
  z-index: 4;
  top: 50%;
  width: 0.4rem;
  height: 0.4rem;
  padding: 0;
  transform: translateY(-50%);
  border: 1px solid rgba(232, 201, 138, 0.32);
  border-radius: 50%;
  color: #e8c98a;
  background: rgba(3, 5, 7, 0.72);
  font-size: 0.27rem;
  line-height: 0.34rem;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.boryx-hero__arrow:hover {
  border-color: #e8c98a;
  background: rgba(201, 154, 77, 0.2);
}

.boryx-hero__arrow--prev {
  left: 0.08rem;
}

.boryx-hero__arrow--next {
  right: 0.08rem;
}

.boryx-hero__dots {
  position: absolute;
  z-index: 5;
  left: 50%;
  bottom: -0.26rem;
  display: flex;
  align-items: center;
  gap: 0.08rem;
  transform: translateX(-50%);
}

.boryx-hero__dots button {
  width: 0.08rem;
  height: 0.08rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(217, 221, 227, 0.32);
  cursor: pointer;
  transition: width 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
}

.boryx-hero__dots button.active {
  width: 0.26rem;
  background: #e8c98a;
  box-shadow: 0 0 0.12rem rgba(242, 184, 91, 0.72);
}

.boryx-copy-enter-active,
.boryx-copy-leave-active,
.boryx-poster-enter-active,
.boryx-poster-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.boryx-copy-enter,
.boryx-copy-leave-to {
  opacity: 0;
  transform: translateY(0.12rem);
}

.boryx-poster-enter,
.boryx-poster-leave-to {
  opacity: 0;
  transform: scale(0.975);
}

.boryx-hero__mobile-meta {
  display: none;
}

@media (max-width: 1000px) {
  .boryx-hero {
    padding: 0.12rem 0.24rem 0.22rem;
  }

  .boryx-hero__shell {
    display: block;
    width: 100%;
    min-height: 0;
    padding: 0.08rem;
    border-radius: 0.22rem;
    background: linear-gradient(160deg, #06090e, #020304 72%);
    box-shadow: 0 0.24rem 0.56rem rgba(0, 0, 0, 0.32);
  }

  .boryx-hero__grid {
    display: none;
  }

  .boryx-hero__visual {
    display: block;
  }

  .boryx-hero__poster-frame {
    width: 100%;
    padding: 0;
    border: 0;
    border-radius: 0.16rem;
    background: #020304;
    box-shadow: none;
  }

  .boryx-hero__poster {
    object-fit: contain;
    border-radius: 0.16rem;
  }

  .boryx-hero__frame-corner {
    display: none;
  }

  .boryx-hero__dots {
    bottom: 0.13rem;
    padding: 0.07rem 0.1rem;
    border: 1px solid rgba(232, 201, 138, 0.2);
    border-radius: 999px;
    background: rgba(3, 5, 7, 0.72);
    backdrop-filter: blur(8px);
  }

  .boryx-hero__dots button {
    width: 0.07rem;
    height: 0.07rem;
  }

  .boryx-hero__dots button.active {
    width: 0.22rem;
  }

  .boryx-hero__mobile-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.12rem;
    padding: 0.15rem 0.1rem 0.09rem;
  }

  .boryx-hero__mobile-meta div {
    min-width: 0;
  }

  .boryx-hero__mobile-meta span,
  .boryx-hero__mobile-meta strong {
    display: block;
  }

  .boryx-hero__mobile-meta span {
    overflow: hidden;
    color: #9ca4af;
    font-size: 0.1rem;
    letter-spacing: 0.06em;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .boryx-hero__mobile-meta strong {
    margin-top: 0.04rem;
    color: #f2f3f5;
    font-size: 0.16rem;
    line-height: 1.25;
  }

  .boryx-hero__mobile-meta button {
    flex: 0 0 auto;
    height: 0.36rem;
    padding: 0 0.13rem;
    border: 1px solid #e8c98a;
    border-radius: 0.1rem;
    color: #080a0d;
    background: linear-gradient(135deg, #f0d59d, #c89542);
    font-size: 0.12rem;
    font-weight: 700;
  }
}

@media (prefers-reduced-motion: reduce) {
  .boryx-copy-enter-active,
  .boryx-copy-leave-active,
  .boryx-poster-enter-active,
  .boryx-poster-leave-active {
    transition: none;
  }
}
</style>
